/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { CustomerLoginSchema, type CustomerLoginInput } from '@therapy/shared-validators';
import axios from 'axios';
import { Eye, EyeOff, HeartPulse } from 'lucide-react';
import { useCallback, useState, type SyntheticEvent, type JSX } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { FormField } from '../../components/form/form-field.js';
import { authStore } from '../../lib/stores/auth-store.js';

/** Feature pills on the brand banner (i18n keys under `login.feature.`). */
const FEATURES = [
  'itpBuilder',
  'soapNotes',
  'smartBilling',
  'parentPortal',
  'progressTracking',
] as const;

/**
 * Customer login (Phase 6 / Standalone-2 design): a navy brand banner on the
 * left and a single email + password sign-in on the right. Role selection is
 * server-side from the account's actual roles — no role tabs. Google OAuth is
 * intentionally omitted (PO decision).
 */
export function CustomerLoginPage(): JSX.Element {
  return (
    <div className="flex min-h-screen bg-white dark:bg-slate-900">
      <BrandBanner />
      <LoginForm />
    </div>
  );
}

function BrandBanner(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="from-brand-900 to-brand-600 hidden flex-1 flex-col justify-between bg-gradient-to-br p-12 text-white lg:flex">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
          <HeartPulse className="h-6 w-6" aria-hidden="true" />
        </span>
        <p className="text-xl font-semibold">
          Therapy<span className="font-light text-white/70"> OS</span>
        </p>
      </div>

      <div className="max-w-md">
        <h1 className="text-5xl font-extrabold leading-tight">
          {t('login.brandLine1')}
          <br />
          <span className="text-white/60">{t('login.brandLine2')}</span>
        </h1>
        <p className="mt-5 text-base leading-relaxed text-white/80">{t('login.brandBlurb')}</p>
        <div className="mt-8 flex flex-wrap gap-2">
          {FEATURES.map((f) => (
            <span
              key={f}
              className="rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-sm text-white/85"
            >
              {t(`login.feature.${f}`)}
            </span>
          ))}
        </div>
      </div>

      <p className="text-sm text-white/50">{t('login.tagline')}</p>
    </div>
  );
}

function RememberRow({
  registration,
}: {
  readonly registration: UseFormRegisterReturn;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between">
      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
        <input
          type="checkbox"
          {...registration}
          className="focus-ring text-brand-600 h-4 w-4 rounded border-slate-300"
        />
        {t('login.rememberMe')}
      </label>
      <Link
        to="/forgot-password"
        className="text-brand-600 hover:text-brand-700 text-sm font-medium"
      >
        {t('login.forgotPassword')}
      </Link>
    </div>
  );
}

function LoginForm(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [clinics, setClinics] = useState<readonly { id: string; name: string }[] | null>(null);
  const [mfaNeeded, setMfaNeeded] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CustomerLoginInput>({ resolver: zodResolver(CustomerLoginSchema) });

  const togglePassword = useCallback(() => {
    setShowPassword((v) => !v);
  }, []);

  const submit = useCallback(
    async (values: CustomerLoginInput) => {
      setServerError(null);
      try {
        const result = await authStore.signIn(values);
        switch (result.kind) {
          case 'success':
            await navigate(authStore.homePath);
            break;
          case 'clinic-selection':
            setClinics(result.clinics);
            break;
          case 'mfa-required':
            setMfaNeeded(true);
            break;
        }
      } catch (err) {
        const isRateLimited = axios.isAxiosError(err) && err.response?.status === 429;
        setServerError(t(isRateLimited ? 'login.accountLocked' : 'login.invalidCredentials'));
      }
    },
    [navigate, t],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>) => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  const onPickClinic = useCallback(
    (clinicId: string) => {
      setValue('clinicId', clinicId);
      void handleSubmit(submit)();
    },
    [handleSubmit, setValue, submit],
  );

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="w-full max-w-md">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white">{t('login.welcome')}</h2>
        <p className="mt-1 text-slate-500 dark:text-slate-400">{t('login.subtitle')}</p>

        <form className="mt-8 space-y-5" onSubmit={onFormSubmit} noValidate>
          <FormField
            id="email"
            type="email"
            label={t('login.email')}
            autoComplete="email"
            placeholder={t('login.emailPlaceholder')}
            error={errors.email?.message}
            registration={register('email')}
          />
          <FormField
            id="password"
            type={showPassword ? 'text' : 'password'}
            label={t('login.password')}
            autoComplete="current-password"
            placeholder={t('login.passwordPlaceholder')}
            error={errors.password?.message}
            registration={register('password')}
            trailing={<PasswordToggle visible={showPassword} onToggle={togglePassword} />}
          />

          <RememberRow registration={register('rememberMe')} />

          {mfaNeeded && (
            <FormField
              id="totp"
              type="text"
              label={t('staffLogin.totp')}
              autoComplete="one-time-code"
              inputMode="numeric"
              placeholder={t('staffLogin.totpPlaceholder')}
              error={errors.totp?.message}
              registration={register('totp')}
            />
          )}

          {clinics !== null && <ClinicPicker clinics={clinics} onPick={onPickClinic} />}

          {serverError !== null && (
            <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {serverError}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="focus-ring bg-brand-600 hover:bg-brand-700 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
          >
            {t('login.signIn')}
          </button>

          <p className="text-center text-sm text-slate-500 dark:text-slate-400">
            {t('login.noAccount')}{' '}
            <span className="text-brand-600 font-semibold">{t('login.contactAdmin')}</span>
          </p>
        </form>
      </div>
    </div>
  );
}

interface ClinicPickerProps {
  readonly clinics: readonly { id: string; name: string }[];
  readonly onPick: (clinicId: string) => void;
}

/** Second login step when one email belongs to multiple clinics. */
function ClinicPicker({ clinics, onPick }: ClinicPickerProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <fieldset className="rounded-xl border border-slate-200 p-3">
      <legend className="px-1 text-sm font-medium text-slate-700">{t('login.selectClinic')}</legend>
      <div className="space-y-2">
        {clinics.map((clinic) => (
          <ClinicOption key={clinic.id} clinic={clinic} onPick={onPick} />
        ))}
      </div>
    </fieldset>
  );
}

function ClinicOption({
  clinic,
  onPick,
}: {
  readonly clinic: { id: string; name: string };
  readonly onPick: (clinicId: string) => void;
}): JSX.Element {
  const handleClick = useCallback(() => {
    onPick(clinic.id);
  }, [clinic.id, onPick]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className="focus-ring hover:border-brand-400 hover:bg-brand-50 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-sm font-medium"
    >
      {clinic.name}
    </button>
  );
}

interface PasswordToggleProps {
  readonly visible: boolean;
  readonly onToggle: () => void;
}

function PasswordToggle({ visible, onToggle }: PasswordToggleProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={visible ? 'Hide password' : 'Show password'}
      className="focus-ring absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
    >
      {visible ? (
        <EyeOff className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Eye className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}
